import { Divider, Typography } from "@mui/material";
import { Box } from "@mui/system";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IAppointmentEntryEX } from "~/web-shared-components/interfaces/IAppointmentEntryEX";

import { AsnAgentStateEnum } from "../../../web-shared-components/asn1/EUCSrv/stubs/ENetUC_Agent";
import { AsnReachabilityEnum } from "../../../web-shared-components/asn1/EUCSrv/stubs/ENetUC_Common_AsnContact";
import { IContactContainer } from "../../../web-shared-components/interfaces/interfaces";
import { PresenceIconWithTooltip } from "../../../web-shared-components/react-components/presenceIconWithTooltip/PresenceIconWithTooltip";
import { addSipPrefixIfNotExists } from "../../lib/common";
import ContactHelper from "../../lib/ContactHelper";
import { ConstrContactFromModel } from "../../modelconstructors/ConstrModelContact";
import { ConstrContentDetailsFromJson } from "../../modelconstructors/ConstrModelContent";
import { FnCreateEmptyContact, ICombiBlock, IContact } from "../../models/ModelContact";
import { IContentDetails } from "../../models/ModelContent";
import { IClientContent } from "../../zustand/contactSlice";
import { useStore } from "../../zustand/store";
import { Header } from "../common/Header/Header";
import { StatusInfo } from "../ContactList/ContactListItem";
import { ContactBlock } from "../imported/ContactElements/ContactBlock";

interface IContactDetailsProps {
	contactID: string | null;
	onBack?: () => void;
	onClose?: () => void;
}

export const ContactDetails = ({ contactID, onBack, onClose }: IContactDetailsProps) => {
	const { t } = useTranslation();
	const customLabels = useStore((state) => state.customLabels);
	const cDetails = useStore((state) => state.contactDetails);
	const contactsDetails = useStore((state) => state.contactsDetails);
	const contacts = useStore((state) => state.contactsPresence);
	const asnDataBaseContact = contactsDetails.get(contactID ?? "");
	const statusInfos: IContactContainer | undefined = contacts.get(contactID ?? "");

	const [contactDetails, setContactDetails] = useState<IContact>();
	const [currentAppointments, setCurrentAppointments] = useState<IAppointmentEntryEX[]>([]);
	const [nextAppointment, setNextAppointment] = useState<string>("");
	const [callNote, setCallNote] = useState<string>("");

	useEffect(() => {
		let content: IContentDetails;
		let iContact: IContact;
		let selectedContact: IClientContent | undefined;
		if (!contactID) return;

		selectedContact = contactsDetails.get(contactID);
		if (!selectedContact) selectedContact = contactsDetails.get(addSipPrefixIfNotExists(contactID || ""));

		if (selectedContact) {
			content = ConstrContentDetailsFromJson(selectedContact);
			content.statusInfos = {
				currentAppointments: statusInfos?.currentAppointments,
				isMobileAvailable: false,
				remoteContact: undefined,
				u8sNote: statusInfos?.customNote || "",
				presence: statusInfos?.presence || 0,
				nextAppointment: statusInfos?.nextAppointment,
				reachability: statusInfos?.absentState?.iReachability || AsnReachabilityEnum.eREACHABILITYNORMAL,
				agentState: statusInfos?.agentState || AsnAgentStateEnum.eAGENTSTATENOAGENT,
				seqCalls: statusInfos?.seqCalls
			};

			iContact = ConstrContactFromModel(content, customLabels.CustLabels, cDetails.ContactDetails, "contactDetails");

			const callNote = iContact?.statusInfos?.u8sNote?.startsWith("IDS_") ? t(iContact.statusInfos.u8sNote) : "";
			setCallNote(callNote);
		} else iContact = FnCreateEmptyContact();

		setContactDetails(iContact);

		const currentAppointmentTime = statusInfos
			? ContactHelper.getCurrentValidAppointments(statusInfos.currentAppointments)
			: [];
		const nextAppointmentTime = statusInfos ? ContactHelper.getNextAppointment(statusInfos.nextAppointment) : "";

		setCurrentAppointments(currentAppointmentTime);
		setNextAppointment(nextAppointmentTime);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		contactID,
		contactsDetails,
		customLabels,
		cDetails,
		statusInfos?.presence,
		statusInfos?.nextAppointment,
		statusInfos?.absentState?.iReachability,
		statusInfos?.agentState
	]);

	if (!contactDetails) return null;

	return (
		<>
			<Header title={t("IDS_CONTACT_DETAILS")} onBack={onBack} onClose={onClose} />
			<Box height={"100%"} overflow="auto">
				<Box sx={{ display: "flex", flexDirection: "row" }} px={2}>
					<PresenceIconWithTooltip
						contact={{ ...statusInfos, contactID: contactID || "", asnNetDatabaseContact: asnDataBaseContact }}
						contactDetails={cDetails}
						customLabels={customLabels}
						combiPic={ContactHelper.getPic(asnDataBaseContact)}
						currentAppointments={currentAppointments}
						nextAppointment={nextAppointment}
						mouseHover={() => {}}
					/>
					<Box sx={{ flexDirection: "column", paddingLeft: "16px" }}>
						<Typography>{contactDetails.displayName}</Typography>
						<Box>
							{StatusInfo({
								statusInfo: contactDetails.statusInfos || 0,
								note: contactDetails.statusInfos.u8sNote || "",
								nextAppointment:
									contactDetails.statusInfos.currentAppointments && contactDetails.statusInfos.currentAppointments[0]
										? t("IDS_APPOINTMENT") + " - " + contactDetails.statusInfos.currentAppointments[0].u8sSubject
										: "",
								callNote
							})}
						</Box>
					</Box>
				</Box>
				{contactDetails?.combiBlocks.map((combiBlock: ICombiBlock, index: number) => {
					return (
						<Fragment key={"rf" + index}>
							<ContactBlock key={index} block={combiBlock} type="ch" />
							<Divider sx={{ mx: 1 }} />
						</Fragment>
					);
				})}
			</Box>
		</>
	);
};
